/**
 * 风格卡片选择面板：从建节点菜单点"风格卡片"时弹出。
 *
 * 网格布局展示所有已上线的系统风格卡片（封面 + 名称），
 * 选中后在指定画布坐标创建 style_card 节点。
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Palette, X } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import { boardActions } from '../state/boardStore';
import { makeNode } from '../utils';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { useMenuExternalDismiss } from '../hooks/useMenuExternalDismiss';
import type { StyleCardNodeData } from '../types';

interface StyleCardItem {
  id: string;
  name: string;
  prompt: string;
  coverUrl: string | null;
  category: string;
}

interface StyleCardPickerProps {
  canvasX: number;
  canvasY: number;
  onClose: () => void;
}

export function StyleCardPicker({ canvasX, canvasY, onClose }: StyleCardPickerProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [cards, setCards] = useState<StyleCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('全部');

  useMenuExternalDismiss(menuRef, onClose);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformFetch('/canvas/style-cards');
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.data)) {
          setCards(data.data);
        }
      } catch {
        // 静默失败，用户可关闭重试
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = ['全部', ...new Set(cards.map((c) => c.category))];
  const filtered = activeCategory === '全部' ? cards : cards.filter((c) => c.category === activeCategory);

  const handleSelect = (card: StyleCardItem) => {
    const nodeData: StyleCardNodeData = {
      styleId: card.id,
      name: card.name,
      coverUrl: card.coverUrl ?? '',
      prompt: card.prompt,
      category: card.category,
    };
    const node = makeNode('style_card', { x: 0, y: 0 }, nodeData);
    node.x = canvasX - node.width / 2;
    node.y = canvasY - node.height / 2;
    boardActions.addNode(node);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        ref={menuRef}
        className="flex max-h-[80vh] w-[600px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-purple-500" />
            <span className="text-sm font-semibold text-slate-800">选择风格卡片</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 分类筛选 */}
        {categories.length > 1 && (
          <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-4 py-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition ${
                  activeCategory === cat
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* 网格 */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400">
              <Palette className="mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm">暂无风格卡片</p>
              <p className="mt-1 text-xs">管理员审核通过的风格卡片会出现在这里</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {filtered.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleSelect(card)}
                  className="group relative aspect-[4/5] overflow-hidden rounded-lg border border-slate-200 transition hover:border-purple-300 hover:shadow-md"
                >
                  {card.coverUrl ? (
                    <img
                      src={resolveBrowserMediaUrl(card.coverUrl) || card.coverUrl}
                      alt={card.name}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                      draggable={false}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-purple-100 to-violet-200">
                      <Palette className="h-6 w-6 text-purple-300" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 pt-6">
                    <span className="truncate text-xs font-medium text-white">{card.name}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
